import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import {
  FolderLock,
  FolderPlus,
  Lock,
  Pencil,
  Search,
  Tag,
  Trash2,
} from "lucide-react";
import { staffApi } from "@client/api/staff";
import type { Category } from "@client/api/types";
import { StaffShell } from "@client/components/StaffShell";
import { Badge } from "@client/components/ui/badge";
import { Button } from "@client/components/ui/button";
import { Input } from "@client/components/ui/input";
import { Textarea } from "@client/components/ui/textarea";
import { Label } from "@client/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@client/components/ui/dialog";

export const Route = createFileRoute("/_authenticated/categories")({
  component: CategoriesPage,
});

function CategoriesPage() {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [search, setSearch] = useState("");

  // Edit category modal state
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");

  // Delete category confirmation state
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(null);

  const {
    data: categories,
    error: categoriesError,
    isPending: categoriesPending,
  } = useQuery({
    queryKey: ["categories"],
    queryFn: staffApi.categories,
  });

  const create = useMutation({
    mutationFn: async () =>
      staffApi.createCategory({
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
      }),
    onSuccess: ({ category }) => {
      toast.success("Category created successfully");
      setName("");
      setDescription("");
      qc.setQueryData<Category[]>(["categories"], (current = []) =>
        [...current.filter((item) => item.id !== category.id), category].sort(
          (a, b) =>
            Number(b.is_system) - Number(a.is_system) ||
            a.name.localeCompare(b.name),
        ),
      );
      void qc.invalidateQueries({ queryKey: ["categories"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const update = useMutation({
    mutationFn: async () => {
      if (!editingCategory) return;
      return staffApi.updateCategory(editingCategory.id, {
        name: editName.trim(),
        description: editDescription.trim(),
      });
    },
    onSuccess: (res) => {
      if (res?.category) {
        toast.success("Category updated successfully");
        const updated = res.category;
        qc.setQueryData<Category[]>(["categories"], (current = []) =>
          current
            .map((item) => (item.id === updated.id ? updated : item))
            .sort(
              (a, b) =>
                Number(b.is_system) - Number(a.is_system) ||
                a.name.localeCompare(b.name),
            ),
        );
        setEditingCategory(null);
        void qc.invalidateQueries({ queryKey: ["categories"] });
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: async (categoryId: string) =>
      staffApi.deleteCategory(categoryId),
    onSuccess: () => {
      toast.success("Category deleted");
      setDeletingCategory(null);
      void qc.invalidateQueries({ queryKey: ["categories"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const handleOpenEdit = (category: Category) => {
    setEditingCategory(category);
    setEditName(category.name);
    setEditDescription(category.description || "");
  };

  const filteredCategories = (categories ?? []).filter((c) => {
    if (!search.trim()) return true;
    const query = search.toLowerCase();
    return (
      c.name.toLowerCase().includes(query) ||
      (c.description && c.description.toLowerCase().includes(query))
    );
  });

  return (
    <StaffShell
      title="Categories Management"
      subtitle="Organize the question bank into official WKF fixed categories and custom disciplines"
    >
      <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
        {/* Left Column: Create New Category */}
        <form
          className="panel space-y-4 p-5 h-fit"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim().length < 2) {
              toast.error("Category name must be at least 2 characters long");
              return;
            }
            create.mutate();
          }}
        >
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <FolderPlus className="h-5 w-5" />
            </div>
            <div>
              <h2 className="hall-heading text-lg font-bold">New Category</h2>
              <p className="text-xs text-muted-foreground">Add a custom examination group</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="category-name">Category Name</Label>
            <Input
              id="category-name"
              placeholder="e.g. Kata Technical Criteria"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={create.isPending}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="category-desc">Description (Optional)</Label>
            <Textarea
              id="category-desc"
              rows={3}
              placeholder="Brief summary of syllabus or rules covered..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={create.isPending}
            />
          </div>

          <Button
            type="submit"
            className="w-full font-semibold"
            disabled={create.isPending || !name.trim()}
          >
            {create.isPending ? "Creating Category…" : "Create Category"}
          </Button>
        </form>

        {/* Right Column: Categories List */}
        <div className="panel p-5 space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="hall-heading text-lg font-bold">All Categories</h2>
              <p className="text-xs text-muted-foreground">
                {categories?.length ?? 0} total categories ({categories?.filter((c) => c.is_system).length ?? 0} fixed system)
              </p>
            </div>

            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search categories…"
                className="pl-8 text-sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {categoriesPending ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              Loading categories…
            </div>
          ) : null}

          {categoriesError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              {categoriesError.message}
            </div>
          ) : null}

          {!categoriesPending && !categoriesError && (
            <div className="grid gap-3 sm:grid-cols-2">
              {filteredCategories.map((c) => (
                <div
                  key={c.id}
                  className={`group relative flex flex-col justify-between rounded-lg border p-4 transition-all duration-200 hover:shadow-sm ${
                    c.is_system
                      ? "border-amber-500/30 bg-amber-500/[0.03]"
                      : "border-border bg-card hover:border-border/80"
                  }`}
                >
                  <div className="space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-foreground text-base tracking-tight">
                          {c.name}
                        </span>
                      </div>
                      {c.is_system ? (
                        <Badge
                          variant="outline"
                          className="shrink-0 border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400 font-medium text-[11px] gap-1 px-2 py-0.5"
                        >
                          <Lock className="h-3 w-3" />
                          Fixed
                        </Badge>
                      ) : (
                        <Badge
                          variant="secondary"
                          className="shrink-0 text-[11px] gap-1 px-2 py-0.5 font-medium"
                        >
                          <Tag className="h-3 w-3 text-muted-foreground" />
                          Custom
                        </Badge>
                      )}
                    </div>

                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {c.description || "No description provided."}
                    </p>
                  </div>

                  {/* Actions Footer */}
                  <div className="mt-4 pt-3 border-t border-border/50 flex items-center justify-between text-xs">
                    <span className="text-[11px] text-muted-foreground">
                      {c.is_system ? "Protected system rulebook" : "User-defined category"}
                    </span>

                    <div className="flex items-center gap-1.5">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs hover:bg-muted"
                        onClick={() => handleOpenEdit(c)}
                        title="Edit category"
                      >
                        <Pencil className="h-3.5 w-3.5 mr-1" />
                        Edit
                      </Button>

                      {c.is_system ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled
                          className="h-7 px-2 text-xs opacity-40 cursor-not-allowed text-muted-foreground hover:bg-transparent"
                          title="Fixed system categories cannot be deleted"
                        >
                          <FolderLock className="h-3.5 w-3.5 mr-1" />
                          Locked
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => setDeletingCategory(c)}
                          title="Delete category"
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {!filteredCategories.length && !categoriesPending && (
                <div className="col-span-full py-12 text-center text-sm text-muted-foreground border border-dashed rounded-lg">
                  {search ? "No categories matching your search query." : "No categories found."}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit Category Dialog */}
      <Dialog
        open={Boolean(editingCategory)}
        onOpenChange={(open) => !open && setEditingCategory(null)}
      >
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-4 w-4 text-primary" />
              Edit Category
            </DialogTitle>
            <DialogDescription>
              {editingCategory?.is_system ? (
                <span className="text-amber-600 dark:text-amber-400 font-medium">
                  Editing a fixed system category. Deletion is restricted, but name and description may be updated.
                </span>
              ) : (
                "Update the category title and syllabus description."
              )}
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (editName.trim().length < 2) {
                toast.error("Category name must be at least 2 characters");
                return;
              }
              update.mutate();
            }}
            className="space-y-4 py-2"
          >
            <div className="space-y-2">
              <Label htmlFor="edit-name">Category Name</Label>
              <Input
                id="edit-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                disabled={update.isPending}
                placeholder="e.g. Kata - WKF 07/2026"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-desc">Description</Label>
              <Textarea
                id="edit-desc"
                rows={3}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                disabled={update.isPending}
                placeholder="Category details..."
              />
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditingCategory(null)}
                disabled={update.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={update.isPending || !editName.trim()}
              >
                {update.isPending ? "Saving Changes…" : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(deletingCategory)}
        onOpenChange={(open) => !open && setDeletingCategory(null)}
      >
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <Trash2 className="h-4 w-4" />
              Delete Category
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the category{" "}
              <strong>"{deletingCategory?.name}"</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="gap-2 sm:gap-0 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeletingCategory(null)}
              disabled={remove.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => {
                if (deletingCategory) {
                  remove.mutate(deletingCategory.id);
                }
              }}
            >
              {remove.isPending ? "Deleting…" : "Delete Category"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </StaffShell>
  );
}
